import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { articles, categories, site } from "@/mocks/portal";

const navItems = [
  { label: "首页", to: "/", end: true },
  ...categories.map((c) => ({ label: c.name, to: `/column/${c.slug}`, end: false })),
  { label: "关于我们", to: "/about", end: false },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [kw, setKw] = useState("");
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const boxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
    setKw("");
  }, [pathname]);

  useEffect(() => {
    if (searchOpen) boxRef.current?.focus();
  }, [searchOpen]);

  const results = useMemo(() => {
    const q = kw.trim().toLowerCase();
    if (!q) return [];
    return articles
      .filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.summary.toLowerCase().includes(q) ||
          a.tags.some((t) => t.toLowerCase().includes(q))
      )
      .slice(0, 6);
  }, [kw]);

  const goArticle = (id: string) => {
    navigate(`/article/${id}`);
    setSearchOpen(false);
    setKw("");
  };

  const isActive = (item: { to: string; end: boolean }) =>
    item.end ? pathname === item.to : pathname.startsWith(item.to);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled || open
          ? "bg-background-50/90 backdrop-blur border-b border-background-200/70"
          : "bg-background-50/40 border-b border-transparent"
      }`}
    >
      <div className="mx-auto w-full max-w-[1180px] px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-4 md:h-[72px]">
          {/* 品牌 */}
          <Link to="/" className="flex items-center gap-2.5 whitespace-nowrap">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50 font-heading text-lg font-bold">
              料
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-black tracking-tight text-foreground-950">
                {site.name}
              </span>
              <span className="mt-1 text-[11px] text-foreground-500">{site.slogan}</span>
            </span>
          </Link>

          {/* 桌面导航 */}
          <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive(item)
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* 右侧操作 */}
          <div className="flex items-center gap-1.5">
            <div className="relative hidden md:block">
              <button
                type="button"
                onClick={() => setSearchOpen((v) => !v)}
                className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-600"
                aria-label="搜索"
              >
                <i className="ri-search-line text-lg" />
              </button>
              {searchOpen && (
                <div className="absolute right-0 top-11 w-80 rounded-xl border border-background-200 bg-background-50 p-2 shadow-none">
                  <div className="flex items-center gap-2 rounded-lg bg-background-100 px-3 py-2">
                    <i className="ri-search-line text-base text-foreground-500" />
                    <input
                      ref={boxRef}
                      value={kw}
                      onChange={(e) => setKw(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && results.length > 0 && goArticle(results[0].id)}
                      placeholder="搜剧集、话题、栏目…"
                      className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                    />
                    {kw && (
                      <button type="button" onClick={() => setKw("")} aria-label="清空">
                        <i className="ri-close-circle-fill text-foreground-300 hover:text-foreground-500" />
                      </button>
                    )}
                  </div>
                  {kw && results.length === 0 && (
                    <p className="px-3 py-3 text-sm text-foreground-500">没有找到相关内容，换个关键词试试。</p>
                  )}
                  {results.length > 0 && (
                    <ul className="nice-scroll mt-1 max-h-72 overflow-auto">
                      {results.map((r) => (
                        <li key={r.id}>
                          <button
                            type="button"
                            onClick={() => goArticle(r.id)}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left hover:bg-background-100"
                          >
                            <span className="text-xs text-foreground-400">{r.date}</span>
                            <span className="line-clamp-1 text-sm text-foreground-800">{r.title}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <Link
              to="/about#subscribe"
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
            >
              订阅内容
            </Link>

            {/* 移动端菜单按钮 */}
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 lg:hidden"
              onClick={() => setOpen((v) => !v)}
              aria-label="打开菜单"
            >
              <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* 移动端下拉面板 */}
      {open && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-2 lg:hidden">
          <div className="mb-3 flex items-center gap-2 rounded-lg bg-background-100 px-3 py-2 md:hidden">
            <i className="ri-search-line text-base text-foreground-500" />
            <input
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && results.length > 0 && goArticle(results[0].id)}
              placeholder="搜索内容…"
              className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
            />
          </div>
          {kw && results.length > 0 && (
            <ul className="mb-2 rounded-lg border border-background-100 bg-background-100/50 p-1">
              {results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => goArticle(r.id)}
                    className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm text-foreground-800 hover:bg-background-100"
                  >
                    <span className="line-clamp-1">{r.title}</span>
                    <span className="whitespace-nowrap text-xs text-foreground-400">{r.date}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <nav className="flex flex-col" aria-label="移动端导航">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-lg px-3 py-2.5 text-base ${
                  isActive(item) ? "bg-primary-50 text-primary-700" : "text-foreground-800 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/about#subscribe"
              className="mt-2 inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50"
            >
              订阅内容
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}